import { useEffect, useState, type RefObject } from "react";

export interface ArticleHeading {
  id: string;
  text: string;
  // 1 for the article's top heading level, 2 below it, ...
  depth: number;
}

// A long article's contents earn a table of contents from this many headings
export const MIN_TABLE_OF_CONTENTS_HEADINGS = 3;

const slugify = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "section";

/**
 * The headings (h1-h3) of an article's rendered content, with ids to link to (the editor's own, or one made from the
 * heading's text when it has none). The editor renders the content after mounting, so this watches the container and
 * reads the headings once they appear (and again if the content changes).
 */
export function useArticleHeadings(containerRef: RefObject<HTMLElement | null>, content: string | undefined): ArticleHeading[] {
  const [headings, setHeadings] = useState<ArticleHeading[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !content) {
      setHeadings([]);
      return;
    }

    const collect = () => {
      const elements = Array.from(container.querySelectorAll<HTMLElement>("h1, h2, h3")).filter((el) => el.textContent?.trim());
      const topLevel = Math.min(...elements.map((el) => Number(el.tagName[1])));
      const used = new Set<string>();
      const found = elements.map((el) => {
        let id = el.id;
        if (!id) {
          const base = `section-${slugify(el.textContent ?? "")}`;
          id = base;
          for (let n = 2; used.has(id) || document.getElementById(id); n++) id = `${base}-${n}`;
          el.id = id;
        }
        // Clear the portal's header when jumped to
        el.style.scrollMarginTop = "6rem";
        used.add(id);
        return { id, text: el.textContent!.trim(), depth: Number(el.tagName[1]) - topLevel + 1 };
      });
      setHeadings((prev) =>
        prev.length === found.length && prev.every((h, i) => h.id === found[i].id && h.text === found[i].text) ? prev : found
      );
    };

    collect();
    const observer = new MutationObserver(collect);
    observer.observe(container, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [containerRef, content]);

  return headings;
}
